<template>
  <div>
    <b-row>
      <b-col class="mb-3" cols="12">
        <h1>Эффективность отделов</h1>
      </b-col>
    </b-row>

    <!-- Фильтры -->
    <b-card class="mb-3">
      <b-card-body class="py-2">
        <b-row class="align-items-end">
          <!-- Квартал -->
          <b-col md="3">
            <CustomDropdown
              v-model="filters.quarter"
              :options="quarterOptions"
              label="Квартал"
              placeholder="Выберите квартал"
              @change="onQuarterChange"
            />
          </b-col>

          <!-- Месяц -->
          <b-col md="3">
            <CustomDropdown
              v-model="filters.month"
              :options="monthOptions"
              label="Месяц"
              placeholder="Выберите месяц"
              null-text="Все месяца квартала"
              :allow-null="true"
              @change="onFiltersChange"
            />
          </b-col>

          <!-- Отдел -->
          <b-col md="3" v-if="!isToManager && !isLogisticsManager && !isLogisticsMultimManager">
            <CustomDropdown
              ref="departmentDropdown"
              v-model="filters.department_id"
              :options="departmentOptions"
              label="Отдел"
              placeholder="Выберите отдел"
              @change="onFiltersChange"
            />
          </b-col>

          <!-- Индикатор загрузки -->
          <b-col md="3" class="d-flex align-items-center mb-2">
            <b-spinner v-if="loading" small class="mr-2" />
            <small v-if="loading" class="text-muted">Загрузка...</small>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Результаты -->
    <b-card v-if="reportData.length > 0">
      <b-card-header class="py-2">
        <h6 class="mb-0">Результаты отчета</h6>
      </b-card-header>
      <b-card-body class="py-2">
        <!-- Таблица результатов -->
        <b-table
          :items="reportData"
          :fields="tableFields"
          striped
          hover
          responsive
          class="mb-4"
          small
          :per-page="pagination.perPage"
          :current-page="pagination.currentPage"
          :total-rows="pagination.totalRows"
          paginated
        >
          <template #cell(successful_deliveries)="row">
            <span 
              class="clickable-number text-primary"
              :class="{ 'has-orders': row.item.successful_deliveries > 0 }"
              @click="showOrdersModal(row.item, 'successful')"
              :title="`Показать ${row.item.successful_deliveries} успешных поставок`"
            >
              {{ row.item.successful_deliveries }}
            </span>
          </template>
          <template #cell(total_deliveries)="row">
            <span 
              class="clickable-number text-primary"
              :class="{ 'has-orders': row.item.total_deliveries > 0 }"
              @click="showOrdersModal(row.item, 'all')"
              :title="`Показать ${row.item.total_deliveries} всех поставок`"
            >
              {{ row.item.total_deliveries }}
            </span>
          </template>
          <template #cell(efficiency_percentage)="row">
            <span 
              class="font-weight-bold efficiency-badge"
              :class="row.item.efficiency_color_class"
            >
              {{ formatPercentage(row.item.efficiency_percentage) }}
            </span>
          </template>
        </b-table>

        <!-- Пагинация -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <span class="mr-2">Показать по:</span>
            <b-form-select
              v-model="pagination.perPage"
              :options="perPageOptions"
              size="sm"
              class="mr-3"
              style="width: 80px;"
              @change="onPerPageChange"
            />
          </div>
          <b-pagination
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :per-page="pagination.perPage"
            size="sm"
            @change="onPageChange"
          />
        </div>

        <!-- Легенда эффективности -->
        <b-alert variant="light" show class="pl-0 pr-0 mb-3">
          <div v-if="!(filters.department_id === 3)">
            <h6 class="mb-2">Шкала эффективности:</h6>
            <div class="d-flex flex-wrap">
              <div v-for="range in efficiencyScale.standard.ranges" :key="range.class" class="mr-3 mb-1">
                  <span class="efficiency-badge" :class="range.class">{{ range.label }}</span>
              </div>
            </div>
          </div>
          <div v-if="hasLogisticsDepartment" class="mt-2">
            <h6 class="mb-2">Шкала для логистики:</h6>
            <div class="d-flex flex-wrap">
              <div v-for="range in efficiencyScale.logistics.ranges" :key="range.class" class="mr-3 mb-1">
                <span class="efficiency-badge" :class="range.class">{{ range.label }}</span>
              </div>
            </div>
          </div>
        </b-alert>

        <!-- Итоги -->
        <b-alert variant="info" show class="py-2">
          <div class="d-flex justify-content-between align-items-center">
            <span class="font-weight-bold">Итоги:</span>
            <div>
              <span class="mr-3">Сотрудников: <strong>{{ summary.total_employees }}</strong></span>
              <span class="mr-3">Успешных поставок: <strong>{{ summary.total_successful_deliveries }}</strong></span>
              <span class="mr-3">Всего поставок: <strong>{{ summary.total_deliveries }}</strong></span>
              <span>Средняя эффективность: <strong>{{ formatPercentage(summary.average_efficiency) }}</strong></span>
            </div>
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Сообщение об отсутствии данных -->
    <b-alert v-else-if="!loading && reportData.length === 0" variant="warning" show>
      По выбранным критериям данные не найдены.
    </b-alert>

    <!-- OrderList компонент -->
    <OrderList
      v-if="showOrderList"
      :openOrderList="showOrderList"
      :orderList="selectedOrders"
      :label="ordersModalTitle"
    />

    <!-- Информация о кварталах -->
    <div class="mt-4">
      <small class="text-muted">
        <strong>Кварталы считаются по следующим месяцам:</strong><br>
        Q1: декабрь, январь, февраль | 
        Q2: март, апрель, май | 
        Q3: июнь, июль, август | 
        Q4: сентябрь, октябрь, ноябрь
      </small>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import CustomDropdown from "@/components/CustomDropdown.vue";
import OrderList from "@/components/orders/OrderList.vue";
import { 
  API_DEPARTMENT_EFFICIENCY_DATA,
  API_DEPARTMENT_EFFICIENCY_FILTER_DATA
} from "@/constants";

export default {
  name: "DepartmentEfficiencyReport",
  
  components: {
    CustomDropdown,
    OrderList
  },

  data() {
    return {
      loading: false,
      loadTimeout: null,
      reportData: [],
      pagination: {
        currentPage: 1,
        perPage: 25,
        totalRows: 0
      },
      perPageOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      summary: {
        total_employees: 0,
        total_successful_deliveries: 0,
        total_deliveries: 0,
        average_efficiency: 0
      },
      efficiencyScale: {
        logistics: {
          name: 'Логистика',
          ranges: []
        },
        standard: {
          name: 'Стандартная',
          ranges: []
        }
      },
      // Данные для OrderList компонента
      selectedOrders: [],
      showOrderList: false,
      ordersModalTitle: '',
      filters: {
        quarter: null,
        month: null,
        department_id: 3  // По умолчанию отдел логистики
      },
      departmentOptions: [],
      quarterOptions: [],
      monthOptions: []
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser", "statusUser", "headDepartment", "roleNoHead"]),
    
    isToManager() {
      return this.currentUser.role.includes("TO-manager");
    },

    isLogisticsManager() {
      return this.currentUser.role.includes("logistics-manager");
    },

    isLogisticsMultimManager() {
      return this.currentUser.role.includes("logistics-multim-manager");
    },

    tableFields() {
      return [
        { key: 'employee_name', label: 'Сотрудник', sortable: true },
        { key: 'successful_deliveries', label: 'Успешные поставки', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total_deliveries', label: 'Все поставки', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'efficiency_percentage', label: 'Эффективность, %', sortable: true, thClass: 'text-center', tdClass: 'text-center' }
      ];
    },

    hasLogisticsDepartment() {
      return this.reportData.some(user => user.department_id === 3);
    }
  },

  watch: {
    'filters.department_id'(newVal) {
      // Отдел изменился
    },
    
    departmentOptions: {
      handler(newOptions) {
        if (newOptions.length > 0 && this.filters.department_id === 3) {
          // Принудительно обновляем значение для корректного отображения
          this.$nextTick(() => {
            // Временно устанавливаем null, затем обратно 3 для принудительного обновления
            this.filters.department_id = null;
            this.$nextTick(() => {
              this.filters.department_id = 3;
              
              // Принудительно обновляем компонент
              if (this.$refs.departmentDropdown) {
                this.$refs.departmentDropdown.$forceUpdate();
              }
            });
          });
        }
      },
      immediate: true
    }
  },

  async created() {
    // Генерируем опции кварталов
    this.generateQuarterOptions();
    
    // Устанавливаем текущий квартал по умолчанию
    this.setCurrentQuarter();
    
    // Генерируем опции месяцев для текущего квартала
    this.generateMonthOptions();
    
    // Загружаем данные фильтров
    await this.loadFilterData();
    
    // Принудительно устанавливаем отдел логистики
    this.filters.department_id = 3;
    
    // Автоматически загружаем данные после установки фильтров
    this.loadReportData();
  },

  methods: {
    generateQuarterOptions() {
      const options = [];
      const now = new Date();
      const currentYear = now.getFullYear();
      
      // Генерируем последние 8 кварталов
      for (let i = 0; i < 8; i++) {
        const quarterDate = new Date(currentYear, now.getMonth() - (i * 3), 1);
        const year = quarterDate.getFullYear();
        const quarter = Math.floor(quarterDate.getMonth() / 3) + 1;
        
        options.push({
          value: `${year}-Q${quarter}`,
          text: `${year} Q${quarter}`
        });
      }
      
      this.quarterOptions = options;
    },

    setCurrentQuarter() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1; // getMonth() возвращает 0-11, нам нужно 1-12
      
      // Финансовая схема кварталов (год начинается с декабря):
      // Q1: декабрь, январь, февраль (месяцы 12, 1, 2)
      // Q2: март, апрель, май (месяцы 3, 4, 5)
      // Q3: июнь, июль, август (месяцы 6, 7, 8)
      // Q4: сентябрь, октябрь, ноябрь (месяцы 9, 10, 11)
      
      let quarter;
      // Финансовая схема кварталов (год начинается с декабря):
      // Q1: декабрь, январь, февраль (12,1,2)
      // Q2: март, апрель, май (3,4,5)
      // Q3: июнь, июль, август (6,7,8)
      // Q4: сентябрь, октябрь, ноябрь (9,10,11)
      if (month === 12 || month === 1 || month === 2) {
        quarter = 1; // Q1: дек, янв, фев
        // Для декабря используем следующий год
        if (month === 12) {
          const nextYear = year + 1;
          this.filters.quarter = `${nextYear}-Q${quarter}`;
          return;
        }
      } else if (month >= 3 && month <= 5) {
        quarter = 2; // Q2: мар, апр, май
      } else if (month >= 6 && month <= 8) {
        quarter = 3; // Q3: июн, июл, авг
      } else if (month >= 9 && month <= 11) {
        quarter = 4; // Q4: сен, окт, ноя
      }
      
      this.filters.quarter = `${year}-Q${quarter}`;
    },

    generateMonthOptions() {
      if (!this.filters.quarter) {
        this.monthOptions = [];
        return;
      }

      const [year, quarter] = this.filters.quarter.split('-Q');
      const quarterNum = parseInt(quarter);
      
      const options = [];
      const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
      
      // Финансовая схема кварталов (год начинается с декабря):
      // Q1: декабрь, январь, февраль (месяцы 12, 1, 2)
      // Q2: март, апрель, май (месяцы 3, 4, 5)
      // Q3: июнь, июль, август (месяцы 6, 7, 8)
      // Q4: сентябрь, октябрь, ноябрь (месяцы 9, 10, 11)
      
      let months;
      if (quarterNum === 1) {
        months = [12, 1, 2]; // дек, янв, фев
      } else if (quarterNum === 2) {
        months = [3, 4, 5]; // мар, апр, май
      } else if (quarterNum === 3) {
        months = [6, 7, 8]; // июн, июл, авг
      } else if (quarterNum === 4) {
        months = [9, 10, 11]; // сен, окт, ноя
      }
      
      months.forEach(monthNum => {
        let displayYear = year;
        if (monthNum === 12) {
          // Декабрь относится к предыдущему году для Q1
          displayYear = parseInt(year) - 1;
        } else if (monthNum === 1 || monthNum === 2) {
          // Январь и февраль относятся к текущему году для Q1
          displayYear = parseInt(year);
        }
        
        options.push({
          value: `${displayYear}-${String(monthNum).padStart(2, '0')}`,
          text: `${monthNames[monthNum - 1]} ${displayYear}`
        });
      });
      
      this.monthOptions = options;
    },

    async loadFilterData() {
      try {
        const response = await axios.get(API_DEPARTMENT_EFFICIENCY_FILTER_DATA);
        if (response.data.success) {
          const data = response.data.data;
          
          // Загружаем отделы
          this.departmentOptions = data.departments.map(dept => ({
            value: dept.id,
            text: dept.name
          }));
          
          // Принудительно устанавливаем отдел логистики после загрузки опций
          this.$nextTick(() => {
            this.filters.department_id = 3;
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки данных фильтров:', error);
        this.$bvToast.toast('Ошибка загрузки данных фильтров', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    async loadReportData() {
      if (!this.filters.quarter) {
        this.$bvToast.toast('Выберите квартал', {
          title: 'Предупреждение',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.loading = true;
      
      try {
        const params = {
          quarter: this.filters.quarter
        };

        // Если выбран месяц, добавляем его в параметры
        if (this.filters.month) {
          params.month = this.filters.month;
        }

        // Если выбран отдел, добавляем его в параметры
        if (this.filters.department_id) {
          params.department_id = this.filters.department_id;
        }

        const response = await axios.get(API_DEPARTMENT_EFFICIENCY_DATA, { params });
        
        if (response.data.success) {
          this.reportData = response.data.data;
          this.summary = response.data.summary;
          this.efficiencyScale = response.data.efficiency_scale || this.efficiencyScale;
          
          // Устанавливаем общее количество записей для пагинации
          this.pagination.totalRows = this.reportData.length;
          this.pagination.currentPage = 1; // Сбрасываем на первую страницу
        } else {
          this.$bvToast.toast('Ошибка загрузки данных отчета', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error);
        this.$bvToast.toast('Ошибка загрузки данных отчета', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    onQuarterChange() {
      // Сбрасываем выбор месяца при смене квартала
      this.filters.month = null;
      // Генерируем опции месяцев для выбранного квартала
      this.generateMonthOptions();
      // Перезагружаем данные
      this.onFiltersChange();
    },

    onFiltersChange() {
      // Автоматически перезагружаем данные при изменении фильтров с небольшой задержкой
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
      }
      this.loadTimeout = setTimeout(() => {
        this.loadReportData();
      }, 300);
    },

    formatPercentage(value) {
      if (value === null || value === undefined) return '-';
      return `${value.toFixed(1)}%`;
    },

    showOrdersModal(employee, type) {
      const orders = type === 'successful' ? employee.successful_orders : employee.all_orders;
      
      
      if (!orders || orders.length === 0) {
        this.$bvToast.toast('Заказы не найдены', {
          title: 'Информация',
          variant: 'info',
          solid: true
        });
        return;
      }
      
      this.selectedOrders = orders;
      this.ordersModalTitle = `${type === 'successful' ? 'Успешные' : 'Все'} поставки - ${employee.employee_name}`;
      this.showOrderList = true;
    },

    resetOrdersModal() {
      this.selectedOrders = [];
      this.showOrderList = false;
      this.ordersModalTitle = '';
    },

    // Методы пагинации
    onPageChange(page) {
      this.pagination.currentPage = page;
      // При смене страницы можно добавить логику для загрузки данных
      // если данные загружаются с сервера с пагинацией
    },

    onPerPageChange() {
      this.pagination.currentPage = 1; // Сбрасываем на первую страницу
      // При смене количества записей на странице можно добавить логику
    },

    getPaginationInfo() {
      const start = (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
      const end = Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.totalRows);
      return `Показано ${start}-${end} из ${this.pagination.totalRows} записей`;
    },

  },

  beforeDestroy() {
    // Очищаем таймаут при уничтожении компонента
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.card-body {
  padding: 0.75rem;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-size: 0.875rem;
}

.table td {
  font-size: 0.875rem;
}

.alert ul {
  margin-bottom: 0;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

h5 {
  font-size: 1rem;
}

/* Стили для бейджей эффективности с эффектом стекла */
.efficiency-badge {
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  min-width: 50px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

/* Стандартная шкала для всех отделов кроме логистики */
.efficiency-low {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.3);
}

.efficiency-medium {
  background: rgba(255, 193, 7, 0.1);
  color: #856404;
  border-color: rgba(255, 193, 7, 0.3);
}

.efficiency-good {
  background: rgba(13, 98, 71, 0.1);
  color: #0D6247;
  border-color: rgba(13, 98, 71, 0.3);
}

/* Специальная шкала для отдела логистики */
.efficiency-very-low {
  background: rgba(220, 53, 69, 0.15);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.4);
}

.efficiency-excellent {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border-color: rgba(40, 167, 69, 0.3);
}

/* Стили для кликабельных цифр */
.clickable-number {
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.1s ease;
}

.clickable-number:hover {
  color: #0056b3 !important;
  text-decoration: none;
  font-weight: bold;
}

/* Жирное выделение для ссылок с заказами */
.clickable-number.has-orders {
  font-weight: bold;
  font-size: 1.05rem;
}

.clickable-number.has-orders:hover {
  font-weight: 900;
}
</style>
